import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './RestaurantSelection.css';
import { buildApiUrl } from '../../config/api.config';

const RestaurantSelection = ({ showAlert }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const currentRestaurantId = localStorage.getItem('currentRestaurantId');

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(buildApiUrl('restaurant/public/list'));
      if (response.data.success) {
        setRestaurants(response.data.restaurants);
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      if (showAlert) showAlert('Could not load restaurants', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (restaurant) => {
    localStorage.setItem('currentRestaurantId', restaurant._id);
    localStorage.setItem('currentRestaurantName', restaurant.name);
    localStorage.setItem('currentRestaurantLogo', restaurant.logo || '');
    
    // Link user to restaurant in backend
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(buildApiUrl('users/link-restaurant'), 
          { restaurantId: restaurant._id },
          { headers: { 'auth-token': token } }
        );
      }
    } catch (error) {
      console.error('Error linking user to restaurant:', error);
    }

    if (showAlert) showAlert(`Selected: ${restaurant.name}`, 'success');
    navigate('/');
  };

  const filtered = restaurants.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.location.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="rs-page-container">
        <div className="rs-loading-overlay">
          <div className="rs-spinner"></div>
          <p className="rs-loading-text">Discovering the best tastes for you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rs-page-container">
      <div className="rs-ambient-bg"></div>
      
      <main className="rs-content-wrapper">
        <header className="rs-header-section">
          <span className="rs-badge">TastyFlow Network</span>
          <h1 className="rs-main-title">Select Your <span>Restaurant</span></h1>
          <p className="rs-sub-text">Join the culinary revolution. Choose a restaurant to begin your gourmet journey.</p>
        </header>

        <section className="rs-search-section">
          <div className="rs-search-box">
            <i className="fa-solid fa-magnifying-glass rs-search-icon"></i>
            <input
              type="text"
              className="rs-search-input"
              placeholder="Search by name, cuisine or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </section>

        {filtered.length === 0 ? (
          <div className="rs-not-found">
            <div className="rs-not-found-icon">
              <i className="fa-solid fa-utensils"></i>
            </div>
            <h3>No results for "{search}"</h3>
            <p>Try exploring different keywords or check back later.</p>
            <button className="rs-reset-btn" onClick={() => setSearch('')}>Clear Search</button>
          </div>
        ) : (
          <div className="rs-restaurant-grid">
            {filtered.map((restaurant, index) => (
              <div
                key={restaurant._id}
                className={`rs-restaurant-card ${currentRestaurantId === restaurant._id ? 'is-selected' : ''}`}
                onClick={() => handleSelect(restaurant)}
                style={{ '--index': index }}
              >
                <div className="rs-card-glow"></div>
                <div className="rs-logo-container">
                  {restaurant.logo ? (
                    <img src={restaurant.logo} alt={restaurant.name} className="rs-brand-logo" />
                  ) : (
                    <div className="rs-logo-placeholder">
                      {restaurant.name.charAt(0)}
                    </div>
                  )}
                </div>

                <div className="rs-restaurant-details">
                  <h3 className="rs-restaurant-name">{restaurant.name}</h3>
                  <div className="rs-restaurant-meta">
                    <span className="rs-meta-item">
                      <i className="fa-solid fa-location-dot"></i>
                      {restaurant.location}
                    </span>
                    <span className="rs-meta-item">
                      <i className="fa-solid fa-star"></i>
                      4.8
                    </span>
                  </div>
                </div>

                <div className="rs-card-action">
                  {currentRestaurantId === restaurant._id ? (
                    <div className="rs-active-badge">
                      <i className="fa-solid fa-circle-check"></i>
                      Active
                    </div>
                  ) : (
                    <button className="rs-select-button">
                      Enter
                      <i className="fa-solid fa-arrow-right"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default RestaurantSelection;
